/**
 * Origin-aware onboarding classification — the single source of truth for the
 * frontend's two distinct origin concepts.
 *
 * Concept 1 — ORIGIN PRIMING (broad). Whether the AI should be told where the
 *   user came from on their first conversation. The backend mirrors this gate
 *   (backend/config/onboarding_origin.py::is_origin_priming_eligible) to decide
 *   whether to persist `skeleton.origin`. Eligible = any inferable marketing
 *   page, EXCEPT funnels that run their own dedicated onboarding (NSFW, space
 *   packs, template wizards, docs landing).
 *
 * Concept 2 — WELCOME OVERRIDE (narrow, strict subset of priming). Whether to
 *   skip the general onboarding wizard and have the AI greet the user directly.
 *   Eligible = priming-eligible AND (page type is blog/guide/article/tool OR the
 *   funnel is on the cherry-picked allowlist). This is intentionally NOT all
 *   landing pages — use-case/agent/app pages are primed but still see the wizard
 *   unless their funnel is explicitly allowlisted.
 *
 * Invariant: welcomeOverride ⊆ originPriming ⊆ (all funnels − hard exclusions).
 * The subset relationship guarantees the two decisions can never contradict.
 */

export type SignupOrigin = {
  type?: string;
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  path?: string;
  category?: string;
  author?: string;
  published_at?: string;
  modified_at?: string;
  captured_at?: string;
};

/**
 * Canonical marketing page types. Must stay in sync with the backend
 * `ORIGIN_PRIMING_TYPES` (backend/config/onboarding_origin.py) and the
 * path→type map in content-renderer/lib/spaHandoff.ts:inferTypeFromPath.
 */
export const ORIGIN_PRIMING_TYPES = new Set([
  'blog', 'guide', 'tool', 'article', 'usecase', 'agent', 'app',
]);

/** Page types that auto-qualify for the welcome override (strict subset). */
const WELCOME_OVERRIDE_TYPES = new Set(['blog', 'guide', 'article', 'tool']);

/**
 * Infer an origin page type from a marketing path. This is the canonical SPA
 * copy; it MUST mirror content-renderer/lib/spaHandoff.ts:inferTypeFromPath
 * (a parity fixture in onboardingOrigin.test.ts guards against drift). Used to
 * reconstruct a minimal origin from the `landing_path` handoff param when the
 * rich page-captured origin didn't survive transport. Returns null for paths
 * with no meaningful origin (homepage, /pricing, /about, …).
 */
export function inferOriginTypeFromPath(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^\/blogs\/.+/.test(path)) return 'blog';
  if (/^\/guides\/.+/.test(path)) return 'guide';
  if (/^\/tools\/.+/.test(path)) return 'tool';
  if (/^\/gtm\/.+/.test(path)) return 'article';
  if (/^\/for\/.+/.test(path)) return 'usecase';
  if (/^\/agents\/.+/.test(path)) return 'agent';
  if (/^\/apps\/.+/.test(path)) return 'app';
  return null;
}

/**
 * Cherry-picked funnel IDs for individual landing pages that don't qualify via
 * type but should get a contextual AI greeting. CLOSED set — adding a new
 * landing page does NOT automatically qualify it; you must add it here.
 */
const WELCOME_OVERRIDE_FUNNELS = new Set([
  // College essay / essay review (legacy IDs kept for compat)
  'essay_review_v1',
  'college_essay_review',
  // Researchers
  'researchers_landing_v1',
  // Students
  'students_landing_v1',
  // Startups
  'startup_landing_v1',
  // Medical
  'medical_documentation_v1',
  'medical_practice_management_v1',
  // Small business
  'for_small_business_marketing_landing_v1',
  // SPA tool converters
  'fact_checker_v1',
  'manuscript_feedback_v2',
  'blog_creator_v1',
  'blog_creator_showcase_v2',
  'copyeditor_v1',
  // Agent page
  'linkedin_lead_search_v1',
  // Product landings
  'work_landing_v1',
  'getting_started_v1',
]);

/**
 * Funnels that run their OWN dedicated onboarding and must never be primed or
 * overridden. Mirrors backend/config/onboarding_origin.py exclusions.
 *   - NSFW (nsfw_v1) — dedicated procedural prompt
 *   - Space packs (claim-pack, claim-pack:*, claim-toolbox, *_space_v1) — lifecycle hooks
 *   - Template onboarding funnels — dedicated persona wizard
 *   - Docs landing (river_docs_landing_v1) — dedicated flow
 */
const NSFW_FUNNELS = new Set(['nsfw_v1']);
const PACK_FUNNELS = new Set(['claim-pack', 'claim-toolbox']);
const TEMPLATE_ONBOARDING_FUNNELS = new Set([
  'grant_writers_v1',
  'rfp_writers_v1',
  'academic_writers_v1',
  'fanfiction_writer_v1',
  'creative_freedom_v1',
  'private_workspace_v1',
  'hnw_writers_v1',
]);
const OTHER_EXCLUDED_FUNNELS = new Set(['river_docs_landing_v1']);

/**
 * Concept 1 gate: is this funnel eligible for origin priming at all?
 * Returns false for funnels that run their own dedicated onboarding.
 */
export function isOriginPrimingEligible(funnelId: string | null | undefined): boolean {
  if (!funnelId) return true; // no funnel = generic signup, priming is harmless
  if (NSFW_FUNNELS.has(funnelId)) return false;
  if (PACK_FUNNELS.has(funnelId)) return false;
  if (funnelId.startsWith('claim-pack:')) return false;
  if (funnelId.endsWith('_space_v1')) return false;
  if (TEMPLATE_ONBOARDING_FUNNELS.has(funnelId)) return false;
  if (OTHER_EXCLUDED_FUNNELS.has(funnelId)) return false;
  return true;
}

/**
 * Concept 2 gate: should we skip the wizard and have the AI greet the user
 * directly? A strict subset of origin priming.
 *
 * @param origin   - User.skeleton.origin (null/undefined for homepage signups)
 * @param funnelId - User.skeleton.funnel_id
 */
export function isWelcomeOverrideEligible(
  origin: SignupOrigin | null | undefined,
  funnelId?: string | null,
): boolean {
  // No origin means homepage/default signup — no override.
  if (!origin) return false;
  // Must be priming-eligible first (enforces the subset invariant).
  if (!isOriginPrimingEligible(funnelId)) return false;
  // 1. Auto-qualify by page type.
  if (origin.type && WELCOME_OVERRIDE_TYPES.has(origin.type)) return true;
  // 2. Cherry-picked funnel allowlist.
  if (funnelId && WELCOME_OVERRIDE_FUNNELS.has(funnelId)) return true;
  return false;
}
